/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'
import loadable from '@loadable/component'
// Components
import {Box, Skeleton, Flex, Container, useDisclosure} from '@chakra-ui/react'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../components/product-quickview-modal'
import {useUnifiedProduct} from '../../hooks/use-unified-product'
import lazyLoadable from '../../utils/lazy-loadable'
// Hooks
import {GenericModal, useGenericModal} from '../../hooks/use-generic-modal'
import useEinstein from '../../hooks/use-einstein'
import CMSContent from '../../components/cms-content'
import PdpCmsModules from './partials/cms-modules'
import ImageGallerySkeleton from '../../components/image-gallery/skeleton'
import useOcapiSite from '../../hooks/useOcapiSite'
import useProductReviewOverview from '../../bazaarvoice-api/hooks/use-product-review-overview'
import useWriteProductReviewModal from '../../bazaarvoice-api/hooks/use-write-product-review-modal'
// Project Components
import EmotionalGallerySkeleton from '../../components/emotional-gallery/emotional-gallery-skeleton'
import ProductUSPSkeleton from './partials/product-usp-skeleton'
// Others/Utils
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {
    getBVReviewProductIds,
    getMasterProductId,
    isCustomizableProductSet,
    isProductSet
} from '../../utils/product-utils'
// constant
import {CRO_EXPERIMENTS, MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../constants'
import Cockpit from './partials/cockpit'
import ProductSetCockpit from './partials/product-set-cockpit'
import CockpitSkeleton from './partials/cockpit-skeleton'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useTrackViewPDP from './hooks/use-track-view-pdp'
import useStructuredData from '../../hooks/use-structured-data'
import Seo from '../../components/seo'
import {trackRRClickStars} from '../../tracking/analytics'
import useEffectEvent from '../../hooks/use-effect-event'
import useStoredConfiguration from './hooks/use-stored-configuration'
import ConfirmationModal from '../../components/confirmation-modal'
import {defineMessage} from 'react-intl'
import CategoriesBreadcrumb from '../../components/breadcrumb/categories-breadcrumb'
import HeaderContent from '../../components/header-content'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useProductDetail from '../../hooks/useProductDetail'
import {Redirect, useParams} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useReviewsModal} from '../../components/reviews-modal'
import {hasBVProductRating} from '../../components/bazaarvoice/utils'
import {useDerivedProduct} from '../../hooks'
import useSlugUrlRedirect from './hooks/use-slug-url-redirect'
import {isServer} from '../../utils/utils'
import RecommendedAccessories from './partials/recommended-accessories'
import Reviews from './partials/reviews'
import useConvertPageTags from '../../convert-api/hooks/use-convert-page-tags'
import {useCroVariantActive} from '../../components/cro'
import ProductDetailsAccordion from './partials/product-details-accordion'
import XleapCockpit from './partials/xleap/cockpit'
import XleapProductUSP from './partials/xleap/usp'
import StickyBar from './partials/xleap/sticky-bar'
import useXleapPdpViewModel from './hooks/use-xleap-pdp-view-model'
import {useProductConfiguratorState} from '../../contexts/product-configurator'
import {NotifyModal} from '../../hooks/use-notify-me-modal'
import ModuleContainer from '../../leap/components/cms/module-container'

// Dynamic modules - use ssr: false to prevent server side rendering
const fallback = <Skeleton height="25vh" width="100%" />
const ProductRecommendations = lazyLoadable(() => import('./partials/product-recommendations'), {
    fallback,
    ssr: false
})
const EmotionalGallery = lazyLoadable(() => import('../../components/emotional-gallery'), {
    fallback: <EmotionalGallerySkeleton />,
    ssr: false
})
const KeyFeatures = lazyLoadable(() => import('../../leap/components/key-features'), {
    fallback: <ProductUSPSkeleton />,
    ssr: false
})
const ZoomModal = lazyLoadable(() => import('./partials/zoom-modal'), {
    fallback: <ImageGallerySkeleton />,
    ssr: false
})
const AccessoryAdder = lazyLoadable(() => import('../../components/accessory-adder'), {
    fallback,
    ssr: false
})
const BackToTopLink = lazyLoadable(() => import('../../components/back-to-top-link'), {
    fallback,
    ssr: false
})
const RecentlyViewed = lazyLoadable(() => import('./partials/recently-viewed'), {
    fallback,
    ssr: false
})
// used for content bellow the fold to be lazy loaded
const LazyCMSContent = lazyLoadable(async () => CMSContent, {
    fallback,
    ssr: false
})
const LazyCMSModules = lazyLoadable(() => import('./partials/cms-modules'), {
    fallback,
    ssr: false
})
const Fanreel = lazyLoadable(() => import('../../components/fanreel'), {
    fallback,
    ssr: false
})
const ProductConfigurator = loadable(() => import('./partials/xleap/configurator'), {
    fallback: <Skeleton height="100dvh" width="100%" />
})

const RESTORE_CONFIGURATION_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Stored configuration found',
        id: 'confirmation_modal.restore_configuration.title.confirm_restore'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Would you like to continue with your selected configuration?',
        id: 'confirmation_modal.restore_configuration.message.confirm_restore'
    })
}

const ProductDetail = () => {
    const {productId: initialProductId} = useParams()
    const {
        master: masterProduct,
        variant: variantProduct,
        isInitialLoading,
        isMasterLoading,
        isVariantLoading,
        isPriceLoading: isPromotionsLoading
    } = useProductDetail(initialProductId)
    const product = useUnifiedProduct(masterProduct, variantProduct)
    const isProductASet = isProductSet(product)
    const einstein = useEinstein()
    const isLoading = isInitialLoading || isMasterLoading
    const {pdpContentEnabled, bvEnabled} = useOcapiSite()
    const {onOpen: onOpenWriteModal} = useWriteProductReviewModal()
    const bvPid = useMemo(() => getBVReviewProductIds(product), [product])
    const hasReviews = useProductReviewOverview(bvPid)?.data?.review
    const derivedProduct = useDerivedProduct(product)
    const {formatMessage} = useIntl()

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            levels: 1
        }
    })

    const {
        onOpen: onOpenReviewModal,
        isOpen: isOpenReviewModal,
        onClose: onCloseReviewModal
    } = useReviewsModal()

    const {productPageJSONLDScript, breadcrumbListJSONLDScript} = useStructuredData(
        category,
        product
    )

    /**************** Error Handling ****************/
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Category Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    /**************** Response Handling ****************/
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.pdp ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    useTrackViewPDP(product)
    // Reviews section is not available for product sets, spare parts and engraving
    const isReviewSectionEnabled = useMemo(
        () => bvEnabled && product && hasBVProductRating(product),
        [bvEnabled, product]
    )
    useInitialDataLayer({
        pageType: 'Product Detail',
        categoryId: product?.primaryCategoryId
    })
    useConvertPageTags({
        pageType: 'product detail',
        productId: getMasterProductId(product) || product?.id
    })
    const parentCategoryTree = useMemo(() => category?.parentCategoryTree || [], [category])
    /**************** Einstein ****************/
    const onSendViewProduct = useEffectEvent((product) => einstein.sendViewProduct(product))
    useEffect(() => {
        if (product) {
            if (product.type.set) {
                onSendViewProduct(product)
                const childrenProducts = product.setProducts
                childrenProducts.map((child) => {
                    onSendViewProduct(child)
                })
            } else {
                onSendViewProduct(product)
            }
        }
    }, [onSendViewProduct, product])
    /**************** Zoom modal ****************/
    const [zoomModal, setZoomModal] = useState()
    const [finalFocusRef, setFinalFocusRef] = useState()
    const zoomModalProps = useGenericModal()
    const showZoomModal = (state) => {
        setFinalFocusRef(state.finalFocusRef)
        setZoomModal(state)
        zoomModalProps.onOpen()
    }

    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    // Hotspot
    const onHotspotClick = ({pid}) => {
        productQuickviewModalProps.onOpen(pid)
    }
    // When open notify me modal close quickview modal
    const onAddToCartClick = () => {
        productQuickviewModalProps.onClose()
    }

    const onRatingsClick = useCallback(() => {
        trackRRClickStars('page top')
        if (hasReviews) {
            onOpenReviewModal()
        } else {
            onOpenWriteModal(bvPid)
        }
    }, [onOpenWriteModal, bvPid, onOpenReviewModal, hasReviews])

    const {
        isAvailable: isStoredConfigurationAvailable,
        apply: applyStoredConfiguration,
        clear: clearStoredConfiguration
    } = useStoredConfiguration({
        product,
        isEnabled: useMemo(
            () =>
                isCustomizableProductSet(product) ||
                product?.c_displayTemplate === 'xpdp-configurator',
            [product]
        )
    })
    const {onOpen: onRestoreConfigurationModalOpen, ...restoreConfigurationModalProps} =
        useDisclosure({
            onClose: clearStoredConfiguration
        })

    const {isBackFromConfigurator} = useProductConfiguratorState()
    useEffect(() => {
        if (isStoredConfigurationAvailable) {
            if (isBackFromConfigurator) {
                // on back navigation from configurator restore automatically if configuration is available
                applyStoredConfiguration()
            } else {
                // otherwise open the confirmation modal
                onRestoreConfigurationModalOpen()
            }
        }
    }, [
        applyStoredConfiguration,
        isStoredConfigurationAvailable,
        onRestoreConfigurationModalOpen,
        isBackFromConfigurator
    ])

    const CockpitComponent = useMemo(() => {
        switch (product?.c_displayTemplate) {
            case 'xpdp':
            case 'xpdp-configurator':
                return XleapCockpit
            // backward compatibility when not defined and customizable-set is handled inside ProductSetCockpit
            case null:
            case undefined:
            case 'default':
            case 'customizable-set':
                return isProductASet ? ProductSetCockpit : Cockpit
            default:
                throw new Error(`Invalid display template: ${product?.c_displayTemplate}`)
        }
    }, [isProductASet, product?.c_displayTemplate])

    const isCro63Active = useCroVariantActive(
        CRO_EXPERIMENTS.cro63PdpHideRating.experimentId,
        CRO_EXPERIMENTS.cro63PdpHideRating.variantId
    )
    const isXleapTemplate =
        product?.c_displayTemplate === 'xpdp' || product?.c_displayTemplate === 'xpdp-configurator'
    const xleapPdpViewModel = useXleapPdpViewModel({
        product,
        onRecommendedProductClick: productQuickviewModalProps.onOpen,
        onBeforeCtaAction: onAddToCartClick,
        isLoading: isInitialLoading || isPromotionsLoading || isVariantLoading
    })

    // redirect to slug url if it's different from the current url
    const redirectTo = useSlugUrlRedirect(isServer, initialProductId, masterProduct)
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    return (
        <div className="sf-product-detail-page" data-testid="product-details-page">
            <Container layerStyle="page" variant="no-padding">
                <Seo
                    title={masterProduct?.pageTitle}
                    description={masterProduct?.pageDescription}
                    keywords={masterProduct?.pageKeywords}
                />
                <Helmet>
                    <meta property="evg:productId" content={product?.id} />
                    <link rel="canonical" href={product?.c_canonicalUrl} />
                    {productPageJSONLDScript}
                    {breadcrumbListJSONLDScript}
                </Helmet>

                <Flex direction="column" alignItems="stretch">
                    {/* CategoriesBreadcrumb */}
                    <CategoriesBreadcrumb categories={parentCategoryTree} />
                    <HeaderContent />
                    {/* Emotional intro */}
                    {product?.c_emotionalIntro && pdpContentEnabled && (
                        <CMSContent id={product.c_emotionalIntro} showError={false} />
                    )}
                </Flex>
            </Container>

            {/* Top Content */}
            {product?.c_pdpTopContent &&
                pdpContentEnabled &&
                (isXleapTemplate ? (
                    <PdpCmsModules assetId={product.c_pdpTopContent} />
                ) : (
                    <Container layerStyle="page" variant="no-padding">
                        <Flex direction="column" alignItems="stretch">
                            <CMSContent id={product.c_pdpTopContent} showError={false} />
                        </Flex>
                    </Container>
                ))}

            {/* Cockpit */}
            {isLoading || !product ? (
                <CockpitSkeleton />
            ) : (
                <CockpitComponent
                    derivedProduct={derivedProduct}
                    product={product}
                    category={parentCategoryTree}
                    isGetPropsLoading={isInitialLoading}
                    isMasterLoading={isMasterLoading}
                    isPromotionsLoading={isPromotionsLoading}
                    isVariantLoading={isVariantLoading}
                    isProductASet={isProductASet}
                    onAddToCartClick={onAddToCartClick}
                    showZoomModal={showZoomModal}
                    onHotspotClick={onHotspotClick}
                    onRatingsClick={onRatingsClick}
                    onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    viewModel={xleapPdpViewModel}
                />
            )}

            {/* Cockpit USP (xleap only) – rendered outside the page Container so it spans full viewport width */}
            {isXleapTemplate && product?.c_pdpCockpitUspContent && (
                <XleapProductUSP
                    contentAssetHtml={product.c_pdpCockpitUspContent}
                    product={product}
                />
            )}

            <Container layerStyle="page" variant="no-padding">
                <Flex direction="column" alignItems="stretch">
                    {/* Mid Content */}
                    {product?.c_pdpMidContent &&
                        pdpContentEnabled &&
                        (isXleapTemplate ? (
                            <LazyCMSModules assetId={product.c_pdpMidContent} />
                        ) : (
                            <LazyCMSContent id={product.c_pdpMidContent} showError={false} />
                        ))}

                    {/* Accessory adder */}
                    <AccessoryAdder
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />

                    {/* Emotional gallery */}
                    <EmotionalGallery
                        imageGroups={product?.imageGroups}
                        selectedVariationAttributes={product?.variationValues}
                        extraCarouselImage={product?.c_extraCarouselImage}
                    />
                </Flex>
            </Container>

            {/* Key Features */}
            <ModuleContainer background="subtle">
                <KeyFeatures product={product} />
            </ModuleContainer>

            {/* Bottom Content */}
            {product?.c_pdpBottomContent &&
                pdpContentEnabled &&
                (product?.c_isLeapPdpBottomContent ? (
                    <LazyCMSModules assetId={product?.c_pdpBottomContent} />
                ) : (
                    <Container layerStyle="page" variant="no-padding">
                        <Flex direction="column" alignItems="stretch">
                            <LazyCMSContent id={product.c_pdpBottomContent} showError={false} />
                        </Flex>
                    </Container>
                ))}

            <Container layerStyle="page" variant="no-padding">
                <Flex direction="column" alignItems="stretch">
                    {/* Reco (Accessories) */}
                    <RecommendedAccessories product={product} />

                    {/* Specifications, What's Included, How To Use, Spare Parts */}
                    <ProductDetailsAccordion
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />

                    {/* Social */}
                    {product?.c_curalate ? <Fanreel product={product} /> : null}

                    {/* Reviews */}
                    {isReviewSectionEnabled && !isCro63Active && (
                        <Reviews
                            product={product}
                            onCloseReviewModal={onCloseReviewModal}
                            isOpenReviewModal={isOpenReviewModal}
                            onOpenReviewModal={onRatingsClick}
                        />
                    )}

                    {/* Bottom Content2 */}
                    {product?.c_pdpBottomContent2 && pdpContentEnabled && (
                        <LazyCMSContent id={product.c_pdpBottomContent2} showError={false} />
                    )}

                    {/* Reco */}
                    <ProductRecommendations
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />

                    {/* Recently viewed */}
                    <RecentlyViewed onRecommendedProductClick={productQuickviewModalProps.onOpen} />

                    {/* Back to top */}
                    <Box background="stokkeGray.bright" paddingTop={4} paddingBottom={10}>
                        <Flex justifyContent="center">
                            <BackToTopLink />
                        </Flex>
                    </Box>
                </Flex>
                <GenericModal
                    {...zoomModalProps}
                    size="full"
                    colorScheme="white"
                    areaLabel={formatMessage({
                        id: 'image_gallery.label',
                        defaultMessage: 'Zoomed-in view of product images'
                    })}
                    onClose={() => {
                        zoomModalProps?.onClose()
                        // works only in case there is a slight delay and it should be more than 100ms
                        setTimeout(() => {
                            finalFocusRef?.focus()
                        }, 500)
                    }}
                >
                    {/* zoom modal is loaded async client side for performance, but the generic modal is already available,
                    so user can open the modal and see placeholder while loading */}
                    <ZoomModal {...zoomModal} product={product} />
                </GenericModal>
                <ProductQuickviewModal {...productQuickviewModalProps} />
                <ConfirmationModal
                    {...restoreConfigurationModalProps}
                    {...RESTORE_CONFIGURATION_CONFIRMATION_DIALOG_CONFIG}
                    onPrimaryAction={applyStoredConfiguration}
                    onAlternateAction={clearStoredConfiguration}
                />
                {isXleapTemplate && <NotifyModal {...xleapPdpViewModel.notifyMe.modalProps} />}
            </Container>
            {!isLoading && isXleapTemplate && (
                <Box
                    position="fixed"
                    bottom="0"
                    left="0"
                    right="0"
                    zIndex="sticky"
                    display={{base: 'block', sm: 'none'}}
                    backgroundColor="primary.base.white"
                >
                    <StickyBar viewModel={xleapPdpViewModel} />
                </Box>
            )}
        </div>
    )
}
ProductDetail.getTemplateName = () => 'product-detail'
ProductDetail.shouldGetProps = () => false

const ProductDetailRouter = () => {
    const {isConfiguratorActive} = useProductConfiguratorState()

    if (isConfiguratorActive) {
        return <ProductConfigurator />
    }

    return <ProductDetail />
}

export default ProductDetailRouter
